import { makeAutoObservable, runInAction } from "mobx";
import type { OnboardingData, OnboardingState } from "../../types";
import { storage } from "../../utils/storage.util";

const STORAGE_KEY = "onboarding" as const;

interface StoredOnboardingData {
  data: OnboardingData | null;
  isCompleted: boolean;
  isSkipped: boolean;
}

export class OnboardingStore implements OnboardingState {
  data: OnboardingData | null = null;
  isCompleted = false;
  isSkipped = false;
  currentStep = 0;

  constructor() {
    makeAutoObservable(this);
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const savedData = storage.get<StoredOnboardingData>(STORAGE_KEY);
      if (savedData) {
        runInAction(() => {
          this.data = savedData.data;
          this.isCompleted = savedData.isCompleted;
          this.isSkipped = savedData.isSkipped;
        });
      }
    } catch (error) {
      // Silently fail - onboarding will start fresh
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    try {
      const dataToSave: StoredOnboardingData = {
        data: this.data,
        isCompleted: this.isCompleted,
        isSkipped: this.isSkipped,
      };
      storage.set(STORAGE_KEY, dataToSave);
    } catch (error) {
      // Silently fail - storage might be full or unavailable
    }
  }

  private clearStorage(): void {
    storage.remove(STORAGE_KEY);
  }

  setOnboardingData(data: OnboardingData): void {
    this.data = data;
    this.isCompleted = true;
    this.isSkipped = false;
    this.saveToStorage();
  }

  skipOnboarding(): void {
    this.isSkipped = true;
    this.isCompleted = false;
    this.saveToStorage();
  }

  resetOnboarding(): void {
    this.data = null;
    this.isCompleted = false;
    this.isSkipped = false;
    this.currentStep = 0;
    this.clearStorage();
  }

  setCurrentStep(step: number): void {
    if (step >= 0) {
      this.currentStep = step;
    }
  }

  nextStep(): void {
    this.currentStep++;
  }

  previousStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  // Computed values
  get hasCompletedOnboarding(): boolean {
    return this.isCompleted || this.isSkipped;
  }

  get onboardingProfile(): OnboardingData | null {
    return this.data;
  }

  get bestStudyPeriod(): string | null {
    return this.data?.bestStudyPeriod || null;
  }

  get learningStyle(): string | null {
    return this.data?.varkPrimary || null;
  }

  get studyPreference(): string | null {
    return this.data?.studyPreference || null;
  }
}

